import { ElementRef, Injectable, NgZone } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  take,
} from 'rxjs';
import {
  ISessionViewState,
  SessionSharedDataService,
  SessionView,
} from 'src/app/services/session-shared-data.service';
import { ItemModel } from 'src/app/models/itemModel';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { UserService } from 'src/app/services/user.service';
import { WebViewerConfigurations } from '../items-canvas/board-items/board-web-viewer-configurartions';
import { BoardIframeConfigurations } from '../items-canvas/board-items/board-iframe-configurations';
import { BoardMarioConfigurations } from '../items-canvas/board-items/board-mario-configurations';
import { VideoCallTileStateManagerService } from './video-call-tile-state-manager.service';

interface fullScreenAppTileState {
  config: FullscreenAppConfigurations;
  visible: boolean;
  appElement: HTMLElement | undefined;
  top: number | undefined;
  left: number | undefined;
  width: number | undefined;
  height: number | undefined;
}

interface FullscreenAppState {
  config: FullscreenAppConfigurations | undefined;
  element: HTMLElement | undefined;
  originalParent: HTMLElement | undefined;
  originalNextSibling: ChildNode | null;
}

export type FullscreenAppConfigurations =
  | WebViewerConfigurations
  | BoardIframeConfigurations
  | BoardMarioConfigurations;

export function isAppFullscreenedInCall(sessionView: ISessionViewState, stateId: string): boolean {
  return (
    sessionView.sessionViewMetaData?.fullscreenId === stateId &&
    isFullScreeningAppInCall(sessionView)
  );
}

export function isFullScreeningAppInCall(sessionView: ISessionViewState): boolean {
  return (
    sessionView.view === SessionView.EXPANDED &&
    !!sessionView.sessionViewMetaData?.fullScreeningAppInCall
  );
}

export function isSupportedFullScreenAppInCall(itemModel: ItemModel): boolean {
  return [ItemModel.WebViewer, ItemModel.IFrame, ItemModel.Mario].includes(itemModel);
}

@UntilDestroy()
@Injectable()
export class FullScreenAppManagerService {
  // manage the case of a full screen app being ready when the call is connected
  private fullscreenAppStateReadyUid?: string;
  private fullscreenAppStateReadySubscription?: Subscription;
  private fullScreenAppStateChangedSubscription?: Subscription;
  private fullScreenAppStateRemovedSubscription?: Subscription;

  private readonly fullScreenAppStateByUid = new Map<string, FullscreenAppState>();
  private readonly _appStateChanged$ = new Subject<FullscreenAppConfigurations>();
  private readonly _appStateRemoved$ = new Subject<string>();

  readonly appStateChanged$ = this._appStateChanged$.asObservable(); // private or public state
  readonly appStateRemoved$ = this._appStateRemoved$.asObservable();

  private attachedAppGalleryUid?: string;
  private temporarilyHiddenAppGalleryUid?: string;
  // True while Document PiP owns the call UI, so the gallery host lives in another document.
  private isFullScreenAppInCallSuspendedByPiP: boolean = false;
  private _appGalleryElementHost?: ElementRef<HTMLDivElement>;

  private updateTilesPosition = new Subject<void>();
  readonly updateTilesPosition$ = this.updateTilesPosition.asObservable();

  fullScreenAppTileState$ = new BehaviorSubject<fullScreenAppTileState | undefined>(undefined);

  fullScreenAppInCallEnabled = this.flagsService.isFlagEnabled(FLAGS.ENABLE_FULLSCREEN_APP_IN_CALL);

  public isFullScreeningAppInCall$(): Observable<boolean> {
    return this.sessionSharedDataService.sessionView.current$.pipe(
      map(
        (sessionViewState) =>
          this.fullScreenAppInCallEnabled && isFullScreeningAppInCall(sessionViewState),
      ),
      distinctUntilChanged(),
    );
  }

  constructor(
    private readonly sessionSharedDataService: SessionSharedDataService,
    private readonly flagsService: FlagsService,
    private readonly userService: UserService,
    private readonly zone: NgZone,
    private readonly videoCallTileStateManagerService: VideoCallTileStateManagerService,
  ) {}

  registerAppState(config: FullscreenAppConfigurations, available?: boolean): void {
    if (!this.fullScreenAppInCallEnabled) {
      return;
    }
    if (!this.canRegisterAppState(config)) {
      return;
    }
    if (!config?.uid) {
      return;
    }
    const registeredConfig = this.getConfigWithAvailability(
      config,
      available ?? this.getRegisteredAppAvailability(config),
    );
    this.setFullScreenAppState(config.uid, { config: registeredConfig });
    this._appStateChanged$.next(registeredConfig);
  }

  // Tracks full screen app element and records its original DOM position.
  registerAppElement(uid: string, itemModel: ItemModel, elementRef: ElementRef<HTMLElement>): void {
    if (!this.fullScreenAppInCallEnabled) {
      return;
    }
    if (!this.canRegisterAppElement(itemModel)) {
      return;
    }
    if (!uid || !elementRef?.nativeElement) {
      return;
    }
    const element = elementRef.nativeElement;
    const currentState = this.fullScreenAppStateByUid.get(uid);
    this.setFullScreenAppState(uid, { element });
    if (!currentState?.originalParent && element.parentElement) {
      this.setFullScreenAppState(uid, {
        originalParent: element.parentElement,
        originalNextSibling: element.nextSibling,
      });
    }
    // Config may already be registered in base app component(ngOnChanges runs before ngAfterViewInit),
    // in which case appStateReady$/updateAppGalleryTileState were already checked and found no element.
    // Re-signal state changed now that the element exists so we retry the gallery tile sync.
    const config = this.getAppState(uid);
    if (config) {
      this._appStateChanged$.next(config);
    }
  }

  // Removes a tracked app element after restoring it to its original position.
  unregisterAppElement(uid: string): void {
    if (!uid) {
      return;
    }
    this.detachFullscreenAppFromGalleryTile(uid);
    this.fullScreenAppStateByUid.delete(uid);
    this._appStateRemoved$.next(uid);
  }

  // Moves a tracked app element into the provided full-screen host container.
  private moveAppElementToGalleryViewTile(uid: string, hostElement: HTMLElement): boolean {
    const appElement = this.getAppElement(uid);
    if (!appElement || !hostElement) {
      return false;
    }
    if (appElement.parentElement !== hostElement) {
      this.reparentAppElementPreserveState(hostElement, appElement, null);
    }
    // to deslect the full screen app element when moved to gallery view
    this.sessionSharedDataService.fabricCanvas?.discardActiveObject();
    return true;
  }

  // Returns a moved app element to the DOM position captured during registration.
  private restoreAppElement(uid: string): void {
    const appState = this.fullScreenAppStateByUid.get(uid);
    const appElement = appState?.element;
    const originalParent = appState?.originalParent;
    if (!appElement || !originalParent || appElement.parentElement === originalParent) {
      return;
    }
    const originalNextSibling = appState.originalNextSibling;
    if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
      this.reparentAppElementPreserveState(
        originalParent,
        appElement,
        originalNextSibling as ChildNode,
      );
      return;
    }
    this.reparentAppElementPreserveState(originalParent, appElement, null);
  }

  /**
   * Reparent DOM using moveBefore where supported so embedded iframe apps
   * keep their browsing history because appendChild / insertBefore move the subtree out of the
   * document and back in, which reloads iframe content.
   * moveBefore supported for (Chrome 133+, Firefox 144+); otherwise we fall back.
   */
  private reparentAppElementPreserveState(
    newParent: HTMLElement,
    movedElement: HTMLElement,
    referenceChild: ChildNode | null,
  ): void {
    type MoveBeforeParent = HTMLElement & {
      moveBefore?: (node: Node, referenceNode: ChildNode | null) => void;
    };
    const movableParent = newParent as MoveBeforeParent;
    const moveBefore = movableParent.moveBefore;
    const canMoveBeforePreserveState =
      typeof moveBefore === 'function' && newParent.ownerDocument === movedElement.ownerDocument;
    if (canMoveBeforePreserveState) {
      moveBefore.call(movableParent, movedElement, referenceChild);
      return;
    }
    if (referenceChild?.parentNode === newParent) {
      newParent.insertBefore(movedElement, referenceChild);
      return;
    }
    newParent.appendChild(movedElement);
  }

  // Gets the native app element currently tracked for the given uid.
  private getAppElement(uid: string): HTMLElement | undefined {
    return this.fullScreenAppStateByUid.get(uid)?.element;
  }

  getAppState(uid: string): FullscreenAppConfigurations | undefined {
    return this.fullScreenAppStateByUid.get(uid)?.config;
  }

  isAppAvailable(uid: string): boolean {
    const appState = this.getAppState(uid);
    if (!appState || !(appState instanceof WebViewerConfigurations)) {
      return true;
    }
    return appState.available;
  }

  // "Ready" requires both the config and the live DOM element: the config alone isn't
  // enough to move the app into a gallery tile
  appStateReady$(uid: string): Observable<FullscreenAppConfigurations> {
    const currentState = this.getAppState(uid);
    if (currentState && this.getAppElement(uid)) {
      return of(currentState);
    }
    return this._appStateChanged$.pipe(
      filter((state) => state.uid === uid && !!this.getAppElement(uid)),
      take(1),
    );
  }

  private isAppFullyRegistered(uid: string): boolean {
    return !!this.getAppState(uid) && !!this.getAppElement(uid);
  }

  getAppAnnotationSizingElement(appUid: string): HTMLElement | undefined {
    return this.getAppVideoElement(appUid) ?? this.getIframeElement(appUid);
  }

  // Handle views functions
  public isFullscreenAppInCallMode(): boolean {
    if (!this.fullScreenAppInCallEnabled) {
      return false;
    }
    const sessionView = this.sessionSharedDataService.sessionView.getCurrentSessionViewState();
    return sessionView.view === SessionView.EXPANDED && isFullScreeningAppInCall(sessionView);
  }

  public moveFullscreenedAppToCallTileIfNeeded(callConnected: boolean): void {
    if (!callConnected) {
      this.detachFullscreenAppStateReadyListener();
      return;
    }
    const currentSessionView =
      this.sessionSharedDataService.sessionView.getCurrentSessionViewState();
    const fullscreenId = currentSessionView.sessionViewMetaData?.fullscreenId;
    if (currentSessionView.view !== SessionView.FULLSCREEN_APP || !fullscreenId) {
      this.detachFullscreenAppStateReadyListener();
      return;
    }
    if (!this.isAppFullyRegistered(fullscreenId)) {
      this.moveFullscreenAppToCallTileWhenStateReady(fullscreenId);
      return;
    }
    this.detachFullscreenAppStateReadyListener();
    this.moveFullscreenAppToCallTile(currentSessionView);
  }

  // move a ready full screen app to call tile when call is connected
  private moveFullscreenAppToCallTileWhenStateReady(fullscreenId: string): void {
    if (this.fullscreenAppStateReadyUid === fullscreenId) {
      return;
    }

    this.detachFullscreenAppStateReadyListener();
    this.fullscreenAppStateReadyUid = fullscreenId;
    this.fullscreenAppStateReadySubscription = this.appStateReady$(fullscreenId)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.detachFullscreenAppStateReadyListener();
        if (!this.videoCallTileStateManagerService.callConnected) {
          return;
        }
        const currentSessionView =
          this.sessionSharedDataService.sessionView.getCurrentSessionViewState();
        const currentFullscreenId = currentSessionView.sessionViewMetaData?.fullscreenId;
        if (
          currentSessionView.view !== SessionView.FULLSCREEN_APP ||
          currentFullscreenId !== fullscreenId
        ) {
          return;
        }
        this.moveFullscreenAppToCallTile(currentSessionView);
      });
  }

  public detachFullscreenAppStateReadyListener(): void {
    this.fullscreenAppStateReadySubscription?.unsubscribe();
    this.fullscreenAppStateReadySubscription = undefined;
    this.fullscreenAppStateReadyUid = undefined;
  }

  moveFullscreenAppToCallTile(currentSessionView: ISessionViewState): void {
    this.sessionSharedDataService.sessionView.switchToSessionView(
      SessionView.EXPANDED,
      undefined,
      undefined,
      {
        ...currentSessionView.sessionViewMetaData,
        fullScreeningAppInCall: true,
      },
    );
  }

  /**
   * Syncs the gallery tile with the app currently fullscreened inside the call.
   *
   * The app temporarily moves its video element into the gallery host.
   * When we leave this mode, lose the fullscreen id, or switch to a
   * different app, the previously attached element must be restored to its
   * original object.
   */
  public updateAppGalleryTileState(): void {
    const currentAppTileState = this.fullScreenAppTileState$.getValue();
    const previousAppUid = currentAppTileState?.config.uid;

    // Teardown: not in-call app fullscreen, or no app id in session metadata.
    const fullscreenId = this.getActiveInCallFullscreenAppId();
    if (!fullscreenId) {
      this.clearAppGalleryTileState(previousAppUid);
      return;
    }

    const appState = this.getAppState(fullscreenId);

    // The session view can update before the target app is rendered locally.
    if (!appState) {
      return;
    }

    if (this.shouldHideAppGalleryTile(appState)) {
      this.hideAppGalleryTileTemporarily(fullscreenId);
      return;
    }

    this.resumeGalleryTileAfterTemporaryHide(appState.uid);

    const isSameApp = currentAppTileState?.config.uid === appState.uid;

    if (previousAppUid && previousAppUid !== appState.uid) {
      this.detachFullscreenAppFromGalleryTile(previousAppUid);
    }

    // Preserve layout and app element only when the same app remains selected.
    this.fullScreenAppTileState$.next(
      this.buildGalleryAppTileState(appState, currentAppTileState, isSameApp),
    );
    this.attachFullscreenAppToGalleryTile();
  }

  /** EXPANDED + fullScreeningAppInCall and sessionViewMetaData.fullscreenId, or undefined. */
  private getActiveInCallFullscreenAppId(): string | undefined {
    if (!this.isFullscreenAppInCallMode()) {
      return undefined;
    }
    return this.sessionSharedDataService.sessionView.getSessionViewMetaData()?.fullscreenId;
  }

  /** After a temporary hide, tile layout expects EXPANDED again (not GALLERY_VIEW). */
  private resumeGalleryTileAfterTemporaryHide(appUid: string): void {
    const wasTemporarilyHidden = this.temporarilyHiddenAppGalleryUid === appUid;
    this.temporarilyHiddenAppGalleryUid = undefined;
    if (wasTemporarilyHidden) {
      this.updateCurrentSessionView(SessionView.EXPANDED);
    }
  }

  /**
   * New tile state for the happy path. Reuses layout and appElement only for the same app;
   * wb-video-call-participant-id fills top/left/width/height on the next layout pass.
   */
  private buildGalleryAppTileState(
    appState: FullscreenAppConfigurations,
    previous: fullScreenAppTileState | undefined,
    isSameApp: boolean,
  ): fullScreenAppTileState {
    return {
      config: appState,
      visible: isSameApp ? previous?.visible ?? false : false,
      appElement: isSameApp ? previous?.appElement : undefined,
      top: isSameApp ? previous?.top : undefined,
      left: isSameApp ? previous?.left : undefined,
      width: isSameApp ? previous?.width : undefined,
      height: isSameApp ? previous?.height : undefined,
    };
  }

  private clearAppGalleryTileState(previousAppUid: string | undefined): void {
    this.fullScreenAppTileState$.next(undefined);
    this.detachFullscreenAppFromGalleryTile(previousAppUid);
  }

  // to handle the cases when the app is removed from the call, becomes unavailable, or private
  // It will also handle late app element registration by calling (updateAppGalleryTileState)
  // as we emit app state changed when app element is registered
  public subscribeToFullscreenAppStateChanges(): void {
    this.fullScreenAppStateChangedSubscription = this.appStateChanged$.subscribe((appState) => {
      this.syncFullscreenAppPrivacyMetadata(appState);
      this.syncAppGalleryTileVisibility(appState.uid);
    });

    this.fullScreenAppStateRemovedSubscription = this.appStateRemoved$.subscribe((uid) =>
      this.closeAppGalleryTileIfActive(uid),
    );
  }

  // to handle the case when host is leading and an app is private
  // then the host full screen the app and change it to public
  private syncFullscreenAppPrivacyMetadata(appState: FullscreenAppConfigurations): void {
    const currentSessionView =
      this.sessionSharedDataService.sessionView.getCurrentSessionViewState();
    const sessionViewMetaData = currentSessionView.sessionViewMetaData;
    if (
      currentSessionView.view !== SessionView.EXPANDED ||
      !isFullScreeningAppInCall(currentSessionView) ||
      sessionViewMetaData.fullscreenId !== appState.uid
    ) {
      return;
    }

    const fullScreeningAPrivateApp = !!appState.isPrivate;
    if (sessionViewMetaData.fullScreeningAPrivateApp === fullScreeningAPrivateApp) {
      return;
    }

    this.sessionSharedDataService.sessionView.switchToSessionView(
      currentSessionView.view,
      currentSessionView.expandedFullScreenChange ?? undefined,
      currentSessionView.openTabs,
      {
        ...sessionViewMetaData,
        fullScreeningAPrivateApp,
      },
    );
  }

  private syncAppGalleryTileVisibility(appUid: string): void {
    if (!this.isFullscreenAppInCallMode()) {
      return;
    }
    const fullscreenId =
      this.sessionSharedDataService.sessionView.getSessionViewMetaData()?.fullscreenId;
    if (fullscreenId !== appUid) {
      return;
    }
    const appState = this.getAppState(appUid);
    if (!appState || this.shouldHideAppGalleryTile(appState)) {
      this.hideAppGalleryTileTemporarily(appUid);
      this.emitUpdateTilesPosition();
      return;
    }

    this.updateAppGalleryTileState();
    this.emitUpdateTilesPosition();
  }

  private closeAppGalleryTileIfActive(appUid: string): void {
    const fullscreenId =
      this.sessionSharedDataService.sessionView.getSessionViewMetaData()?.fullscreenId;
    if (!this.isFullscreenAppInCallMode() || fullscreenId !== appUid) {
      return;
    }
    this.temporarilyHiddenAppGalleryUid = undefined;
    this.clearAppGalleryTileState(appUid);
    this.exitInCallFullscreenAppMode();
    this.emitUpdateTilesPosition();
  }

  /** Leaves in-call app fullscreen and syncs gallery view to session state (not just tile manager). */
  private exitInCallFullscreenAppMode(): void {
    this.updateCurrentSessionView(SessionView.GALLERY_VIEW);
    const currentSessionView =
      this.sessionSharedDataService.sessionView.getCurrentSessionViewState();
    if (!isFullScreeningAppInCall(currentSessionView)) {
      return;
    }
    this.sessionSharedDataService.sessionView.switchToSessionView(SessionView.GALLERY_VIEW);
  }

  private hideAppGalleryTileTemporarily(appUid: string): void {
    this.temporarilyHiddenAppGalleryUid = appUid;
    this.clearAppGalleryTileState(appUid);
    this.updateCurrentSessionView(SessionView.GALLERY_VIEW);
  }

  private shouldHideAppGalleryTile(appState: FullscreenAppConfigurations): boolean {
    return this.shouldHideAppFromCurrentUser(appState);
  }

  public shouldHideAppFromCurrentUser(appState: FullscreenAppConfigurations): boolean {
    return (
      this.flagsService.isFlagEnabled(FLAGS.APPS_PRIVACY_SWITCH) &&
      appState.isPrivate &&
      appState.ownerId !== this.userService.userId
    );
  }

  public updateAppGalleryTileAfterBoardChange(): void {
    if (!this.isFullscreenAppInCallMode()) {
      return;
    }

    this.updateAppGalleryTileState();
    this.emitUpdateTilesPosition();
  }

  public updateFullscreenAppTileState(appUid: string): void {
    const appElement = this.getAppAnnotationSizingElement(appUid);
    const currentFullscreenAppTileState = this.fullScreenAppTileState$.getValue();
    if (!currentFullscreenAppTileState || !appElement) {
      return;
    }

    this.zone.run(() => {
      this.fullScreenAppTileState$.next({
        ...currentFullscreenAppTileState,
        appElement,
      });
    });
  }

  public attachFullscreenAppToGalleryTile(): void {
    if (this.isFullScreenAppInCallSuspendedByPiP) {
      return;
    }
    const appTileState = this.fullScreenAppTileState$.getValue();
    const appUid = appTileState?.config.uid;
    const hostElement = this.appGalleryElementHost?.nativeElement;
    if (!appUid || !hostElement || !this.isFullscreenAppInCallMode()) {
      return;
    }
    const attached = this.moveAppElementToGalleryViewTile(appUid, hostElement);
    if (!attached) {
      return;
    }
    this.attachedAppGalleryUid = appUid;
    this.updateFullscreenAppTileState(appUid);
  }

  public detachFullscreenAppFromGalleryTile(uid?: string): void {
    const appUid = uid ?? this.attachedAppGalleryUid;
    if (!appUid) {
      return;
    }
    this.restoreAppElement(appUid);
    if (this.attachedAppGalleryUid === appUid) {
      this.attachedAppGalleryUid = undefined;
    }
  }

  /**
   * Returns a fullscreen in-call app to the whiteboard before Document PiP moves the call UI,
   * preserving iframe state on the board. Session view stays on EXPANDED.
   *
   * Suspension lasts for the whole PiP lifetime, even when no app is fullscreened yet, because
   * an app fullscreened while PiP is open would otherwise be moved into the PiP document and
   * reload its iframe content.
   */
  public suspendFullscreenAppInCallForDocumentPiP(): void {
    this.isFullScreenAppInCallSuspendedByPiP = true;

    const appUid = this.getActiveInCallFullscreenAppId();
    if (!appUid) {
      return;
    }

    this.fullScreenAppTileState$.next(undefined);
    this.detachFullscreenAppFromGalleryTile(appUid);
    this.temporarilyHiddenAppGalleryUid = undefined;
  }

  /** Re-attaches the app suspended before Document PiP opened back into the gallery tile. */
  public restoreFullscreenAppInCallAfterDocumentPiP(): void {
    this.isFullScreenAppInCallSuspendedByPiP = false;
    if (!this.isFullscreenAppInCallMode()) {
      return;
    }

    this.zone.run(() => {
      this.updateAppGalleryTileState();
      this.emitUpdateTilesPosition();
    });
  }

  set appGalleryElementHost(elementRef: ElementRef<HTMLDivElement> | undefined) {
    this._appGalleryElementHost = elementRef;
  }

  get appGalleryElementHost(): ElementRef<HTMLDivElement> | undefined {
    return this._appGalleryElementHost;
  }

  // Currently only used for web viewer apps
  private getAppVideoElement(appUid: string): HTMLVideoElement | undefined {
    const appElement = this.getAppElement(appUid);
    if (!appElement) {
      return undefined;
    }
    const webViewerEmbedElement = appElement.querySelector('.web-viewer') as
      | HTMLDivElement
      | undefined
      | null;
    const videoElement =
      webViewerEmbedElement?.shadowRoot?.querySelector('video') ??
      webViewerEmbedElement?.querySelector('video') ??
      appElement.querySelector('video');
    return videoElement ?? undefined;
  }

  private getIframeElement(iframeUid: string): HTMLElement | undefined {
    const iframeAppElement = this.getAppElement(iframeUid);
    if (!iframeAppElement) {
      return undefined;
    }
    return iframeAppElement.querySelector('iframe') ?? iframeAppElement;
  }

  // Restores moved apps before clearing their tracked DOM positions.
  reset(): void {
    for (const appUid of this.fullScreenAppStateByUid.keys()) {
      this.restoreAppElement(appUid);
    }
    this.attachedAppGalleryUid = undefined;
    this.temporarilyHiddenAppGalleryUid = undefined;
    this.fullScreenAppTileState$.next(undefined);
    this.fullScreenAppStateByUid.clear();
  }

  private emitUpdateTilesPosition(): void {
    this.zone.run(() => {
      this.updateTilesPosition.next();
    });
  }

  private setFullScreenAppState(uid: string, state: Partial<FullscreenAppState>): void {
    this.fullScreenAppStateByUid.set(uid, {
      config: undefined,
      element: undefined,
      originalParent: undefined,
      originalNextSibling: null,
      ...this.fullScreenAppStateByUid.get(uid),
      ...state,
    });
  }

  public isFullScreenAppInCallEnabled(): boolean {
    return this.fullScreenAppInCallEnabled;
  }

  private updateCurrentSessionView(sessionView: SessionView): void {
    this.videoCallTileStateManagerService.currentSessionView = sessionView;
  }

  private canRegisterAppState(appState: FullscreenAppConfigurations): boolean {
    return (
      appState instanceof WebViewerConfigurations ||
      appState instanceof BoardIframeConfigurations ||
      appState instanceof BoardMarioConfigurations
    );
  }

  private canRegisterAppElement(itemModel: ItemModel): boolean {
    return (
      itemModel === ItemModel.IFrame ||
      itemModel === ItemModel.Mario ||
      itemModel === ItemModel.WebViewer
    );
  }

  private getConfigWithAvailability(
    config: FullscreenAppConfigurations,
    available: boolean,
  ): FullscreenAppConfigurations {
    if (!(config instanceof WebViewerConfigurations) || config.available === available) {
      return config;
    }
    return Object.assign(Object.create(Object.getPrototypeOf(config)), config, {
      available,
    }) as WebViewerConfigurations;
  }

  private getRegisteredAppAvailability(config: FullscreenAppConfigurations): boolean {
    if (!(config instanceof WebViewerConfigurations)) {
      return true;
    }
    const registeredConfig = this.getAppState(config.uid);
    if (registeredConfig instanceof WebViewerConfigurations) {
      return registeredConfig.available;
    }
    return true;
  }

  public isAppFullscreened(uid: string): boolean {
    const sessionViewState = this.sessionSharedDataService.sessionView.getCurrentSessionViewState();
    return (
      (sessionViewState.view === SessionView.FULLSCREEN_APP &&
        sessionViewState.sessionViewMetaData?.fullscreenId === uid) ||
      isAppFullscreenedInCall(sessionViewState, uid)
    );
  }

  private unsubscribeFromFullScreenAppStateChanges(): void {
    this.fullScreenAppStateChangedSubscription?.unsubscribe();
    this.fullScreenAppStateChangedSubscription = undefined;
    this.fullScreenAppStateRemovedSubscription?.unsubscribe();
    this.fullScreenAppStateRemovedSubscription = undefined;
  }

  public cleanupFullScreenAppManager(): void {
    this.detachFullscreenAppStateReadyListener();
    this.unsubscribeFromFullScreenAppStateChanges();
    this.isFullScreenAppInCallSuspendedByPiP = false;
    this.detachFullscreenAppFromGalleryTile();
  }
}
